import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Row, Col, Typography, Button, Space, Spin, Statistic, Tag, Table, Empty, Descriptions, Avatar, Tooltip, message,
} from 'antd';
import {
  ArrowLeftOutlined, ShoppingOutlined, PhoneOutlined, CalendarOutlined,
  GlobalOutlined, MailOutlined, AppstoreOutlined, EyeOutlined,
  UserOutlined, ShopOutlined, DollarOutlined, LinkOutlined, CopyOutlined,
  SendOutlined, ContactsOutlined, RobotOutlined, FunnelPlotOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../services/api';

const { Title, Text } = Typography;

export default function CustomerBackendPage() {
  const { t } = useTranslation();
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await authAPI.getCustomerDashboard(userId);
        setData(res.data);
      } catch (e) {
        console.error('Load customer data failed:', e);
      }
      setLoading(false);
    })();
  }, [userId]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }

  if (!data) {
    return (
      <Card>
        <Empty description={t('customerBackend.loadError')} />
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>{t('customerBackend.back')}</Button>
        </div>
      </Card>
    );
  }

  const { user, scenarios, stats, balance } = data;

  const getSiteUrl = (slug) => `${window.location.origin}/s/${slug}`;

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => message.success(t('customerBackend.linkCopied')));
  };

  const columns = [
    {
      title: t('customerBackend.scenarioName'),
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <a onClick={() => navigate(`/scenarios/${record.id}`)}>{text}</a>
      ),
    },
    {
      title: t('customerBackend.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s) => {
        const map = { draft: { color: 'default', label: t('customerBackend.draft') }, active: { color: 'green', label: t('customerBackend.active') }, archived: { color: 'orange', label: t('customerBackend.archived') } };
        const cfg = map[s] || { color: 'default', label: s };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: t('customerBackend.siteLink'),
      key: 'site_link',
      width: 280,
      render: (_, record) => {
        if (!record.site_config) return <Text type="secondary">{t('customerBackend.notCreated')}</Text>;
        const { slug, is_published } = record.site_config;
        if (!slug) return <Text type="secondary">{t('customerBackend.notSet')}</Text>;
        const url = getSiteUrl(slug);
        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Space size={4}>
              <Tag color={is_published ? 'green' : 'orange'} style={{ margin: 0 }}>
                {is_published ? t('customerBackend.online') : t('customerBackend.draft')}
              </Tag>
              {is_published && (
                <Tooltip title={t('customerBackend.linkCopied')}>
                  <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => copyUrl(url)} />
                </Tooltip>
              )}
            </Space>
            {is_published ? (
              <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, wordBreak: 'break-all' }}>
                <LinkOutlined style={{ marginRight: 4 }} />{url}
              </a>
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>{url}</Text>
            )}
          </Space>
        );
      },
    },
    {
      title: t('customerBackend.productCol'),
      dataIndex: 'product_count',
      key: 'products',
      width: 70,
      align: 'center',
    },
    {
      title: t('customerBackend.inquiryCol'),
      dataIndex: 'inquiry_count',
      key: 'inquiries',
      width: 70,
      align: 'center',
    },
    {
      title: t('customerBackend.eventCol'),
      dataIndex: 'event_count',
      key: 'events',
      width: 70,
      align: 'center',
    },
    {
      title: t('customerBackend.emailCol'),
      dataIndex: 'email_count',
      key: 'emails',
      width: 70,
      align: 'center',
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 240,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/scenarios/${record.id}`)}>
            {t('customerBackend.scenarioEdit')}
          </Button>
          <Button type="link" size="small" icon={<ShopOutlined />} onClick={() => navigate(`/scenarios/${record.id}/site`)}>
            {t('customerBackend.siteManage')}
          </Button>
          {record.site_config?.is_published && record.site_config?.slug && (
            <a href={getSiteUrl(record.site_config.slug)} target="_blank" rel="noopener noreferrer">
              <Button type="link" size="small" icon={<GlobalOutlined />}>
                {t('customerBackend.openFrontend')}
              </Button>
            </a>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Back button */}
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/scenarios')} style={{ marginBottom: 16 }}>
        {t('customerBackend.backToList')}
      </Button>

      {/* Customer info hero */}
      <div style={{
        background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)',
        borderRadius: 16, padding: '28px 32px', marginBottom: 24,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: -30, right: -30, width: 180, height: 180,
          borderRadius: '50%', background: 'rgba(255,255,255,0.08)',
        }} />
        <Row align="middle" gutter={24}>
          <Col>
            <Avatar size={64} icon={<UserOutlined />} style={{ background: 'rgba(255,255,255,0.2)' }} />
          </Col>
          <Col flex="auto">
            <Title level={3} style={{ color: '#fff', margin: 0 }}>
              {user.display_name}
            </Title>
            <Space style={{ marginTop: 4 }}>
              {user.company_name && (
                <Tag style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff' }}>
                  {user.company_name}
                </Tag>
              )}
              <Tag style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff' }}>
                {user.role === 'customer' ? t('customerBackend.roleCustomer') : user.role === 'admin' ? t('customerBackend.roleAdmin') : t('customerBackend.roleManager')}
              </Tag>
              <Tag color={user.is_active ? 'green' : 'red'}>
                {user.is_active ? t('customerBackend.activeStatus') : t('customerBackend.inactiveStatus')}
              </Tag>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Customer details */}
      <Card style={{ marginBottom: 20, borderRadius: 12 }}>
        <Descriptions column={{ xs: 1, sm: 2, md: 4 }} size="small">
          <Descriptions.Item label={t('customerBackend.account')}>{user.username}</Descriptions.Item>
          <Descriptions.Item label="Email">{user.email || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('customerBackend.company')}>{user.company_name || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('customerBackend.createdAt')}>
            {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Stats overview */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} md={4}>
          <Card style={{ borderRadius: 10 }}>
            <Statistic title={t('customerBackend.statsScenarios')} value={stats.total_scenarios} prefix={<AppstoreOutlined style={{ color: '#1890ff' }} />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card style={{ borderRadius: 10 }}>
            <Statistic title={t('customerBackend.statsActive')} value={stats.active_scenarios} prefix={<AppstoreOutlined style={{ color: '#52c41a' }} />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card style={{ borderRadius: 10 }}>
            <Statistic title={t('customerBackend.statsProducts')} value={stats.total_products} prefix={<ShoppingOutlined style={{ color: '#fa8c16' }} />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card style={{ borderRadius: 10 }}>
            <Statistic title={t('customerBackend.statsInquiries')} value={stats.total_inquiries} prefix={<PhoneOutlined style={{ color: '#fa541c' }} />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card style={{ borderRadius: 10 }}>
            <Statistic title={t('customerBackend.statsEvents')} value={stats.total_events} prefix={<CalendarOutlined style={{ color: '#722ed1' }} />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card style={{ borderRadius: 10 }}>
            <Statistic title={t('customerBackend.statsEmails')} value={stats.total_emails} prefix={<MailOutlined style={{ color: '#13c2c2' }} />} />
          </Card>
        </Col>
      </Row>

      {/* Management feature links */}
      <Card
        title={<Space><AppstoreOutlined /> {t('customerBackend.adminTools')}</Space>}
        style={{ marginBottom: 20, borderRadius: 12 }}
        size="small"
      >
        <Row gutter={[12, 12]}>
          {[
            { title: t('customerBackend.toolScenarios'), icon: <AppstoreOutlined />, path: '/scenarios', color: '#1890ff', desc: t('customerBackend.toolScenariosDesc') },
            { title: t('customerBackend.toolSite'), icon: <ShopOutlined />, path: scenarios.length ? `/scenarios/${scenarios[0].id}/site` : '/scenarios', color: '#13c2c2', desc: t('customerBackend.toolSiteDesc') },
            { title: t('customerBackend.toolEmail'), icon: <SendOutlined />, path: '/email-compose', color: '#722ed1', desc: t('customerBackend.toolEmailDesc') },
            { title: t('customerBackend.toolContacts'), icon: <ContactsOutlined />, path: '/contacts', color: '#1890ff', desc: t('customerBackend.toolContactsDesc') },
            { title: t('customerBackend.toolBilling'), icon: <DollarOutlined />, path: '/billing', color: '#fa8c16', desc: t('customerBackend.toolBillingDesc') },
            { title: t('customerBackend.toolChatbot'), icon: <RobotOutlined />, path: '/chatbot', color: '#52c41a', desc: t('customerBackend.toolChatbotDesc') },
            { title: t('customerBackend.toolFunnel'), icon: <FunnelPlotOutlined />, path: '/funnel', color: '#eb2f96', desc: t('customerBackend.toolFunnelDesc') },
            { title: t('customerBackend.toolLanding'), icon: <GlobalOutlined />, path: '/landing-pages', color: '#fa541c', desc: t('customerBackend.toolLandingDesc') },
          ].map(item => (
            <Col xs={12} sm={8} md={6} key={item.title}>
              <Card
                hoverable
                size="small"
                onClick={() => navigate(item.path)}
                style={{ borderRadius: 10, height: '100%', cursor: 'pointer' }}
                styles={{ body: { padding: 14 } }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 8,
                  background: `${item.color}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: item.color, fontSize: 18, marginBottom: 8,
                }}>
                  {item.icon}
                </div>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{item.title}</div>
                <Text type="secondary" style={{ fontSize: 11 }}>{item.desc}</Text>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Quick links for published sites */}
      {scenarios.some(s => s.site_config?.is_published && s.site_config?.slug) && (
        <Card
          title={<Space><LinkOutlined /> {t('customerBackend.customerSiteLinks')}</Space>}
          style={{ marginBottom: 20, borderRadius: 12 }}
          size="small"
        >
          <Row gutter={[16, 12]}>
            {scenarios.filter(s => s.site_config?.is_published && s.site_config?.slug).map(s => {
              const url = getSiteUrl(s.site_config.slug);
              return (
                <Col xs={24} sm={12} key={s.id}>
                  <Card
                    size="small"
                    style={{ borderRadius: 8, background: '#fafafa' }}
                    styles={{ body: { padding: '10px 14px' } }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      {s.site_config.brand_name || s.name}
                    </div>
                    <Space size={8} style={{ width: '100%', flexWrap: 'wrap' }}>
                      <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, wordBreak: 'break-all' }}>
                        <LinkOutlined style={{ marginRight: 4 }} />{url}
                      </a>
                      <Tooltip title={t('customerBackend.linkCopied')}>
                        <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => copyUrl(url)} />
                      </Tooltip>
                    </Space>
                    <div style={{ marginTop: 8 }}>
                      <Space size={4}>
                        <Button size="small" icon={<ShopOutlined />} onClick={() => navigate(`/scenarios/${s.id}/site`)}>
                          {t('customerBackend.backendManage')}
                        </Button>
                        <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/scenarios/${s.id}`)}>
                          {t('customerBackend.scenarioEdit')}
                        </Button>
                        <a href={url} target="_blank" rel="noopener noreferrer">
                          <Button size="small" type="primary" icon={<GlobalOutlined />}>
                            {t('customerBackend.openSite')}
                          </Button>
                        </a>
                      </Space>
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card>
      )}

      {/* Balance card */}
      {balance && (
        <Card
          title={<Space><DollarOutlined /> {t('customerBackend.billingStatus')}</Space>}
          style={{ marginBottom: 20, borderRadius: 12 }}
          size="small"
        >
          <Row gutter={16}>
            <Col xs={12} sm={6}>
              <Statistic title={t('customerBackend.creditsBalance')} value={balance.credits_balance} precision={2} />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic title={t('customerBackend.tokensBalance')} value={balance.tokens_balance} />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic title={t('customerBackend.tokensUsed')} value={balance.tokens_used_total} />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic
                title={t('customerBackend.subscriptionStatus')}
                value={balance.subscription_status === 'active' ? t('customerBackend.subscriptionActive') : balance.subscription_status === 'none' ? t('customerBackend.subscriptionNone') : balance.subscription_status}
                valueStyle={{ color: balance.subscription_status === 'active' ? '#52c41a' : '#999' }}
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* Scenarios with management links */}
      <Card
        title={<Space><AppstoreOutlined /> {t('customerBackend.scenarioList')}</Space>}
        style={{ borderRadius: 12 }}
      >
        {scenarios.length === 0 ? (
          <Empty description={t('customerBackend.noScenarios')} />
        ) : (
          <div>
            {scenarios.map(sc => {
              const siteUrl = sc.site_config?.slug ? getSiteUrl(sc.site_config.slug) : null;
              const statusMap = { draft: { color: 'default', label: t('customerBackend.draft') }, active: { color: 'green', label: t('customerBackend.active') }, archived: { color: 'orange', label: t('customerBackend.archived') } };
              const statusCfg = statusMap[sc.status] || { color: 'default', label: sc.status };
              return (
                <Card
                  key={sc.id}
                  size="small"
                  style={{ marginBottom: 12, borderRadius: 10, border: '1px solid #f0f0f0' }}
                  styles={{ body: { padding: '14px 18px' } }}
                >
                  <Row align="middle" gutter={16}>
                    <Col flex="auto">
                      <Space align="center">
                        <span style={{ fontWeight: 600, fontSize: 15 }}>{sc.name}</span>
                        <Tag color={statusCfg.color}>{statusCfg.label}</Tag>
                        {sc.site_config?.is_published && <Tag color="green">{t('customerBackend.siteOnline')}</Tag>}
                      </Space>
                      {siteUrl && sc.site_config?.is_published && (
                        <div style={{ marginTop: 4 }}>
                          <a href={siteUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#1890ff' }}>
                            <LinkOutlined style={{ marginRight: 4 }} />{siteUrl}
                          </a>
                          <Tooltip title={t('customerBackend.linkCopied')}>
                            <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => copyUrl(siteUrl)} style={{ marginLeft: 4 }} />
                          </Tooltip>
                        </div>
                      )}
                      <div style={{ marginTop: 6, color: '#999', fontSize: 12 }}>
                        {t('customerBackend.productCol')} {sc.product_count} ・ {t('customerBackend.inquiryCol')} {sc.inquiry_count} ・ {t('customerBackend.eventCol')} {sc.event_count} ・ {t('customerBackend.emailCol')} {sc.email_count}
                      </div>
                    </Col>
                    <Col>
                      <Space wrap>
                        <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/scenarios/${sc.id}`)}>
                          {t('customerBackend.scenarioEdit')}
                        </Button>
                        <Button size="small" icon={<ShopOutlined />} onClick={() => navigate(`/scenarios/${sc.id}/site`)}>
                          {t('customerBackend.siteManage')}
                        </Button>
                        <Button size="small" icon={<SendOutlined />} onClick={() => navigate('/email-compose')}>
                          {t('customerBackend.emailSend')}
                        </Button>
                        {siteUrl && sc.site_config?.is_published && (
                          <a href={siteUrl} target="_blank" rel="noopener noreferrer">
                            <Button size="small" type="primary" icon={<GlobalOutlined />}>
                              {t('customerBackend.openFrontend')}
                            </Button>
                          </a>
                        )}
                      </Space>
                    </Col>
                  </Row>
                </Card>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
